/**
 * MaintenanceDashboard — actionable maintenance issues list.
 *
 * Renders every open / assigned / in_progress issue the user can see
 * (RLS does the scoping). Each row exposes severity, room, blocks-
 * readiness flag, age, and the next-step button:
 *   - open       → Start (set status='in_progress')
 *   - assigned   → Start (set status='in_progress')
 *   - in_progress → Mark resolved (status='resolved' with required notes)
 *
 * Resolution notes are required and validated client-side AND by the
 * useUpdateMaintenanceIssue hook; the DB UPDATE is RLS-gated.
 *
 * No room transitions originate from this page. If a resolved issue
 * unblocks a room, the supervisor moves the room to ready via
 * SupervisorDashboard / room-transition (CLAUDE.md ERROR 5 / rule 4).
 */

import { useState, useMemo, type ReactNode } from 'react'
import { useTranslation }                     from 'react-i18next'
import {
  useMaintenanceIssues,
  SEVERITY_RANK,
  type MaintenanceIssueRow,
  type MaintenanceSeverity,
} from '@/hooks/useMaintenanceIssues'
import { useUpdateMaintenanceIssue }     from '@/hooks/useUpdateMaintenanceIssue'
import { useReviewMaintenanceTriage }    from '@/hooks/useReviewMaintenanceTriage'
import { AppError }                      from '@/lib/errors/AppError'

export function MaintenanceDashboard(): ReactNode {
  const { t } = useTranslation()
  const { data, isLoading, error } = useMaintenanceIssues()

  const sorted = useMemo(() => {
    const list = data ?? []
    // Blocking + critical first, then by severity, then by oldest first.
    return [...list].sort((a, b) => {
      if (a.blocks_readiness !== b.blocks_readiness) return a.blocks_readiness ? -1 : 1
      const sev = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
      if (sev !== 0) return sev
      return new Date(a.opened_at).getTime() - new Date(b.opened_at).getTime()
    })
  }, [data])

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('maintenance.title', 'Maintenance issues')}
        </h1>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)' }}>
          {t('maintenance.subtitle', 'Actionable issues — blocking + critical first.')}
        </p>
      </header>

      {isLoading && <Hint>{t('maintenance.loading', 'Loading issues…')}</Hint>}
      {error    && (
        <ErrorPanel
          message={
            error instanceof Error
              ? error.message
              : t('maintenance.failedToLoad', 'Failed to load maintenance issues.')
          }
        />
      )}

      {!isLoading && !error && sorted.length === 0 && (
        <Hint>{t('maintenance.empty', 'No actionable issues. Nice work.')}</Hint>
      )}

      {!isLoading && !error && sorted.length > 0 && (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {sorted.map((row) => <IssueRow key={row.id} row={row} />)}
        </ul>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────

function IssueRow({ row }: { row: MaintenanceIssueRow }): ReactNode {
  const { t }     = useTranslation()
  const update    = useUpdateMaintenanceIssue()
  const review    = useReviewMaintenanceTriage()
  const isResolving = row.status === 'in_progress'
  const [showResolveForm, setShowResolveForm] = useState(false)
  const [notes,           setNotes]           = useState('')
  const [actionError,     setActionError]     = useState<AppError | null>(null)
  const [reviewError,     setReviewError]     = useState<AppError | null>(null)

  const showAiBanner = row.ai_triage_status === 'suggested'
                    && row.ai_triage_reviewed_at === null
  const showAiOutageHint = (row.ai_triage_status === 'unavailable'
                         || row.ai_triage_status === 'malformed')
                       && row.ai_triage_reviewed_at === null

  const onApplyAi = async (): Promise<void> => {
    setReviewError(null)
    try {
      await review.mutateAsync({ issue: row, mode: 'apply' })
    } catch (e) {
      setReviewError(e instanceof AppError ? e : null)
    }
  }
  const onOverrideAi = async (): Promise<void> => {
    setReviewError(null)
    try {
      await review.mutateAsync({ issue: row, mode: 'override' })
    } catch (e) {
      setReviewError(e instanceof AppError ? e : null)
    }
  }

  const isBusy = update.isPending
  const opened = new Date(row.opened_at).toLocaleString()

  const onStart = async (): Promise<void> => {
    setActionError(null)
    try {
      await update.mutateAsync({ issueId: row.id, nextStatus: 'in_progress' })
    } catch (e) {
      setActionError(e instanceof AppError ? e : null)
    }
  }

  const onResolve = async (): Promise<void> => {
    setActionError(null)
    try {
      await update.mutateAsync({
        issueId:         row.id,
        nextStatus:      'resolved',
        resolutionNotes: notes,
      })
      setShowResolveForm(false)
      setNotes('')
    } catch (e) {
      setActionError(e instanceof AppError ? e : null)
    }
  }

  return (
    <li
      style={{
        background:   'var(--brand-white)',
        border:       '1px solid var(--brand-neutral-300)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.75rem 1rem',
        display:      'flex',
        flexDirection:'column',
        gap:          '0.5rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <strong>{row.title}</strong>
            <SeverityBadge severity={row.severity} />
            {row.blocks_readiness && (
              <span
                style={{
                  fontSize:     '0.7rem',
                  padding:      '0.1rem 0.45rem',
                  borderRadius: 'var(--brand-radius-full)',
                  background:   'var(--brand-danger-subtle)',
                  color:        'var(--brand-danger)',
                  fontWeight:   600,
                }}
              >
                {t('maintenance.blocking', 'Blocking')}
              </span>
            )}
          </div>
          <div style={{ marginTop: '0.2rem', color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
            {t('maintenance.roomLine', 'Room {{room}} · {{building}} · opened {{when}}', {
              room:     row.room?.room_number ?? '—',
              building: row.room?.building?.name ?? '—',
              when:     opened,
            })}
          </div>
          {row.description && (
            <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem' }}>{row.description}</p>
          )}
        </div>
      </div>

      {actionError && <ErrorPanel message={actionError.userMessage} />}

      {showAiBanner && (
        <AiTriageBanner
          row={row}
          isBusy={review.isPending}
          onApply={onApplyAi}
          onOverride={onOverrideAi}
          error={reviewError}
        />
      )}
      {showAiOutageHint && (row.ai_triage_status === 'unavailable' || row.ai_triage_status === 'malformed') && (
        <AiOutageHint status={row.ai_triage_status} onDismiss={onOverrideAi} isBusy={review.isPending} />
      )}

      {showResolveForm ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <label htmlFor={`resolve-${row.id}`} style={{ fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
            {t('maintenance.notesLabel', 'Resolution notes (required)')}
          </label>
          <textarea
            id={`resolve-${row.id}`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            disabled={isBusy}
            placeholder={t('maintenance.notesPlaceholder', 'What did you fix?') ?? ''}
            style={{
              padding:      '0.5rem',
              border:       '1px solid var(--brand-neutral-300)',
              borderRadius: 'var(--brand-radius-sm)',
              fontFamily:   'inherit',
              fontSize:     '0.9rem',
              resize:       'vertical',
            }}
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button kind="primary" disabled={isBusy || notes.trim().length === 0} onClick={onResolve}>
              {update.isPending
                ? t('maintenance.resolving', 'Resolving…')
                : t('maintenance.markResolved', 'Mark resolved')}
            </Button>
            <Button kind="secondary" disabled={isBusy} onClick={() => { setShowResolveForm(false); setNotes(''); setActionError(null) }}>
              {t('common.cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isResolving ? (
            <Button kind="primary" disabled={isBusy} onClick={() => { setShowResolveForm(true); setActionError(null) }}>
              {t('maintenance.resolve', 'Resolve')}
            </Button>
          ) : (
            <Button kind="primary" disabled={isBusy} onClick={onStart}>
              {update.isPending
                ? t('maintenance.starting', 'Starting…')
                : t('maintenance.start', 'Start work')}
            </Button>
          )}
        </div>
      )}
    </li>
  )
}

function SeverityBadge({ severity }: { severity: MaintenanceSeverity }): ReactNode {
  const palette: Record<MaintenanceSeverity, { bg: string; fg: string }> = {
    critical: { bg: 'var(--brand-danger-subtle)',  fg: 'var(--brand-danger)'  },
    high:     { bg: 'var(--brand-danger-subtle)',  fg: 'var(--brand-danger)'  },
    medium:   { bg: 'var(--brand-primary-subtle)', fg: 'var(--brand-primary)' },
    low:      { bg: 'var(--brand-neutral-100)',    fg: 'var(--brand-neutral-700)' },
  }
  const c = palette[severity]
  return (
    <span
      data-severity={severity}
      style={{
        fontSize:     '0.7rem',
        padding:      '0.1rem 0.45rem',
        borderRadius: 'var(--brand-radius-full)',
        background:   c.bg,
        color:        c.fg,
        fontWeight:   600,
        textTransform:'capitalize',
      }}
    >
      {severity}
    </span>
  )
}

interface ButtonProps {
  kind:     'primary' | 'secondary'
  disabled: boolean
  onClick:  () => void | Promise<void>
  children: ReactNode
}

function Button({ kind, disabled, onClick, children }: ButtonProps): ReactNode {
  const palette: Record<ButtonProps['kind'], { bg: string; fg: string; border: string }> = {
    primary:   { bg: 'var(--brand-primary)',     fg: 'var(--brand-white)',       border: 'var(--brand-primary)'    },
    secondary: { bg: 'var(--brand-neutral-100)', fg: 'var(--brand-neutral-900)', border: 'var(--brand-neutral-300)' },
  }
  const c = palette[kind]
  return (
    <button
      type="button"
      onClick={() => { void onClick() }}
      disabled={disabled}
      style={{
        background:   c.bg,
        color:        c.fg,
        border:       `1px solid ${c.border}`,
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.5rem 0.9rem',
        fontWeight:   600,
        fontSize:     '0.9rem',
        cursor:       disabled ? 'not-allowed' : 'pointer',
        opacity:      disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  )
}

function AiTriageBanner({
  row,
  isBusy,
  onApply,
  onOverride,
  error,
}: {
  row:        MaintenanceIssueRow
  isBusy:     boolean
  onApply:    () => void | Promise<void>
  onOverride: () => void | Promise<void>
  error:      AppError | null
}): ReactNode {
  const { t } = useTranslation()
  // Apply is disabled when the suggestion is incomplete (no severity or no
  // is_blocking flag). The hook would reject INCOMPLETE_SUGGESTION but we
  // pre-empt the click for clearer UX.
  const applyDisabled = isBusy
                     || row.ai_suggested_severity === null
                     || row.ai_suggested_is_blocking === null

  return (
    <div
      role="region"
      aria-label={t('maintenance.aiTriageBanner', 'AI triage suggestion')}
      data-testid="ai-triage-banner"
      style={{
        background:   'var(--brand-primary-subtle)',
        border:       '1px solid var(--brand-primary)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.6rem 0.75rem',
        display:      'flex',
        flexDirection:'column',
        gap:          '0.4rem',
        fontSize:     '0.85rem',
      }}
    >
      <div style={{ fontWeight: 600, color: 'var(--brand-primary)' }}>
        {t('maintenance.aiSuggests', 'AI suggests')}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.75rem', alignItems: 'center' }}>
        {row.ai_suggested_severity !== null && (
          <span><strong>{t('maintenance.severityLabel', 'Severity')}:</strong> {row.ai_suggested_severity}</span>
        )}
        {row.ai_suggested_category && (
          <span><strong>{t('maintenance.categoryLabel', 'Category')}:</strong> {row.ai_suggested_category.name}</span>
        )}
        {row.ai_suggested_is_blocking !== null && (
          <span>
            <strong>{t('maintenance.blockingLabel', 'Blocking')}:</strong>{' '}
            {row.ai_suggested_is_blocking
              ? t('common.yes', 'Yes')
              : t('common.no',  'No')}
          </span>
        )}
      </div>
      {row.ai_triage_reason && (
        <div style={{ color: 'var(--brand-neutral-700)' }}>{row.ai_triage_reason}</div>
      )}
      {error && <ErrorPanel message={error.userMessage} />}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button kind="primary" disabled={applyDisabled} onClick={onApply}>
          {isBusy
            ? t('maintenance.applying', 'Applying…')
            : t('maintenance.applyAiSuggestion', 'Apply suggestion')}
        </Button>
        <Button kind="secondary" disabled={isBusy} onClick={onOverride}>
          {t('maintenance.overrideAiSuggestion', 'Override')}
        </Button>
      </div>
    </div>
  )
}

function AiOutageHint({
  status,
  onDismiss,
  isBusy,
}: {
  status:    'unavailable' | 'malformed'
  onDismiss: () => void | Promise<void>
  isBusy:    boolean
}): ReactNode {
  const { t } = useTranslation()
  const message = status === 'unavailable'
    ? t('maintenance.aiUnavailable', 'AI triage unavailable — triage manually.')
    : t('maintenance.aiMalformed',   'AI triage returned malformed output — triage manually.')
  return (
    <div
      role="status"
      data-testid="ai-triage-outage"
      data-status={status}
      style={{
        background:   'var(--brand-neutral-100)',
        border:       '1px solid var(--brand-neutral-300)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.5rem 0.75rem',
        fontSize:     '0.8rem',
        color:        'var(--brand-neutral-700)',
        display:      'flex',
        gap:          '0.5rem',
        alignItems:   'center',
        justifyContent: 'space-between',
      }}
    >
      <span>{message}</span>
      <Button kind="secondary" disabled={isBusy} onClick={onDismiss}>
        {t('maintenance.dismissAiHint', 'Dismiss')}
      </Button>
    </div>
  )
}

function Hint({ children }: { children: ReactNode }): ReactNode {
  return <p style={{ color: 'var(--brand-neutral-500)' }}>{children}</p>
}

function ErrorPanel({ message }: { message: string }): ReactNode {
  return (
    <div
      role="alert"
      style={{
        background:   'var(--brand-danger-subtle)',
        color:        'var(--brand-danger)',
        border:       '1px solid var(--brand-danger)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.5rem 0.75rem',
        fontSize:     '0.85rem',
      }}
    >
      {message}
    </div>
  )
}
